// skyhawks-peninsula-internal-web/src/components/Admin/CoachTierDetailModal.js

import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  getCoachTierDetail,
  addSignal,
  deleteSignal,
  setEndorsement,
} from '../../api/tiers';
import Loader from '../common/Loader';
import './CoachTierDetailModal.css';

const POSITIVE_SIGNAL_TYPES = [
  { value: 'direct-kudos', label: 'Direct Kudos' },
  { value: 'google-review', label: 'Google Review' },
  { value: 'peer-support', label: 'Peer Support' },
  { value: 'other', label: 'Other' },
];

const ENDORSEMENT_LEVELS = [
  { value: 'do-not-recommend', label: 'Do Not Recommend' },
  { value: 'recommend', label: 'Recommend' },
  { value: 'strongly-recommend', label: 'Strongly Recommend' },
];

const getTodayDate = () => new Date().toISOString().split('T')[0];

function CoachTierDetailModal({ coachId, coachName, year, season, bonusAmount: propBonusAmount, onClose, onDataChange }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);

  // Complaint form state
  const [complaintDescription, setComplaintDescription] = useState('');
  const [complaintDate, setComplaintDate] = useState(getTodayDate());

  // Positive signal form state
  const [positiveSignalType, setPositiveSignalType] = useState(POSITIVE_SIGNAL_TYPES[0]);
  const [positiveDescription, setPositiveDescription] = useState('');
  const [positiveDate, setPositiveDate] = useState(getTodayDate());

  // Endorsement form state
  const [endorsementLevel, setEndorsementLevel] = useState(null);
  const [endorsementNotes, setEndorsementNotes] = useState('');

  const loadDetail = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const response = await getCoachTierDetail(coachId, year, season);
      if (!mountedRef.current) return;
      if (response?.data) {
        setDetail(response.data);
        if (response.data.endorsement) {
          setEndorsementLevel(
            ENDORSEMENT_LEVELS.find(e => e.value === response.data.endorsement.level)
          );
          setEndorsementNotes(response.data.endorsement.notes || '');
        }
      }
    } catch (err) {
      if (mountedRef.current) toast.error('Failed to load coach details');
    } finally {
      fetchingRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadDetail();
    return () => {
      mountedRef.current = false;
    };
  }, [coachId, year, season]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddComplaint = async () => {
    if (!complaintDescription.trim()) {
      toast.error('Description is required');
      return;
    }

    const data = {
      year,
      season,
      signalType: 'complaint',
      description: complaintDescription,
      date: complaintDate,
    };

    const response = await addSignal(coachId, data);
    if (response?.error) {
      toast.error('Failed to add complaint');
    } else {
      toast.success('Complaint added');
      setComplaintDescription('');
      setComplaintDate(getTodayDate());
      loadDetail();
      onDataChange?.();
    }
  };

  const handleAddPositiveSignal = async () => {
    if (!positiveDescription.trim()) {
      toast.error('Description is required');
      return;
    }

    const data = {
      year,
      season,
      signalType: positiveSignalType.value,
      description: positiveDescription,
      date: positiveDate,
    };

    const response = await addSignal(coachId, data);
    if (response?.error) {
      toast.error('Failed to add positive signal');
    } else {
      toast.success('Positive signal added');
      setPositiveDescription('');
      setPositiveDate(getTodayDate());
      loadDetail();
      onDataChange?.();
    }
  };

  const handleDeleteSignal = async (signalId) => {
    const response = await deleteSignal(signalId);
    if (response?.error) {
      toast.error('Failed to delete signal');
    } else {
      toast.success('Signal deleted');
      loadDetail();
      onDataChange?.();
    }
  };

  const handleSetEndorsement = async () => {
    if (!endorsementLevel) {
      toast.error('Please select an endorsement level');
      return;
    }

    const data = {
      year,
      season,
      level: endorsementLevel.value,
      notes: endorsementNotes,
    };

    const response = await setEndorsement(coachId, data);
    if (response?.error) {
      toast.error('Failed to save review');
    } else {
      toast.success('Review saved');
      loadDetail();
      onDataChange?.();
    }
  };

  const renderOverview = () => {
    if (!detail) return null;
    const { summary, requirements, tier } = detail;
    const bonusAmount = propBonusAmount ?? detail.bonusAmount;

    return (
      <div className="tier-overview">
        <div className="tier-current">
          <span className={`tier-badge tier-${tier}`}>{tier.toUpperCase()}</span>
          <span className="tier-bonus">${bonusAmount.toFixed(2)} bonus</span>
        </div>

        <div className="tier-requirements">
          <h4>Requirements Progress</h4>
          <table className="requirements-table">
            <thead>
              <tr>
                <th>Criteria</th>
                <th>Current</th>
                <th>Varsity</th>
                <th>All-Star</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Programs Completed</td>
                <td>{summary.programsCompleted}</td>
                <td>{requirements.VARSITY.minPrograms}+</td>
                <td>{requirements.ALL_STAR.minPrograms}+</td>
              </tr>
              <tr>
                <td>Positive Signals</td>
                <td>{summary.positiveSignalsCount}</td>
                <td>{requirements.VARSITY.minPositiveSignals}+</td>
                <td>{requirements.ALL_STAR.minPositiveSignals}+</td>
              </tr>
              <tr>
                <td>Complaints</td>
                <td>{summary.complaintsCount}</td>
                <td>0</td>
                <td>0</td>
              </tr>
              <tr>
                <td>End of Season Review</td>
                <td>{summary.endorsementLevel || '—'}</td>
                <td>Recommend</td>
                <td>Strongly Recommend</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="tier-programs">
          <h4>Programs ({detail.programs.length})</h4>
          <ul>
            {detail.programs.map(p => (
              <li key={p.programId} className={p.completed ? 'completed' : 'incomplete'}>
                {p.programTitle} — {p.assignedSessions}/{p.totalSessions} sessions
                {p.hasUnexcusedIssue && <span className="issue-badge">Unexcused Issue</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderComplaints = () => {
    if (!detail) return null;

    const complaints = detail.signals.filter(s => s.signalType === 'complaint');

    return (
      <div className="tier-complaints">
        <div className="add-form">
          <h4>Log Complaint</h4>
          <div className="form-row">
            <input
              type="date"
              value={complaintDate}
              onChange={(e) => setComplaintDate(e.target.value)}
              className="form-input"
            />
          </div>
          <textarea
            value={complaintDescription}
            onChange={(e) => setComplaintDescription(e.target.value)}
            placeholder="Description..."
            rows={3}
          />
          <button onClick={handleAddComplaint} className="add-btn">
            Add Complaint
          </button>
        </div>

        <div className="complaints-list">
          <h4>Complaints ({complaints.length})</h4>
          {complaints.length === 0 ? (
            <p className="empty">No complaints recorded</p>
          ) : (
            <ul>
              {complaints.map(s => (
                <li key={s.id}>
                  <span className="date">{s.date}</span>
                  {s.description}
                  <button onClick={() => handleDeleteSignal(s.id)} className="delete-btn">×</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderPositiveSignals = () => {
    if (!detail) return null;

    const positiveSignals = detail.signals.filter(s => s.signalType !== 'complaint');

    return (
      <div className="tier-positive-signals">
        <div className="add-form">
          <h4>Log Positive Signal</h4>
          <div className="form-row">
            <Select
              value={positiveSignalType}
              onChange={setPositiveSignalType}
              options={POSITIVE_SIGNAL_TYPES}
              className="form-select"
            />
            <input
              type="date"
              value={positiveDate}
              onChange={(e) => setPositiveDate(e.target.value)}
              className="form-input"
            />
          </div>
          <textarea
            value={positiveDescription}
            onChange={(e) => setPositiveDescription(e.target.value)}
            placeholder="Description..."
            rows={3}
          />
          <button onClick={handleAddPositiveSignal} className="add-btn">
            Add Signal
          </button>
        </div>

        <div className="signals-list">
          <h4>Positive Signals ({positiveSignals.length})</h4>
          {positiveSignals.length === 0 ? (
            <p className="empty">No positive signals recorded</p>
          ) : (
            <ul>
              {positiveSignals.map(s => (
                <li key={s.id}>
                  <span className="type-badge">{s.signalType}</span>
                  <span className="date">{s.date}</span>
                  {s.description}
                  <button onClick={() => handleDeleteSignal(s.id)} className="delete-btn">×</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderEndorsement = () => {
    return (
      <div className="tier-endorsement">
        <h4>End of Season Review</h4>
        <p className="endorsement-help">
          Set the review level for this coach for {season} {year}.
        </p>
        <Select
          value={endorsementLevel}
          onChange={setEndorsementLevel}
          options={ENDORSEMENT_LEVELS}
          placeholder="Select review level..."
          className="form-select"
        />
        <textarea
          value={endorsementNotes}
          onChange={(e) => setEndorsementNotes(e.target.value)}
          placeholder="Optional notes..."
          rows={3}
        />
        <button onClick={handleSetEndorsement} className="save-btn">
          Save Review
        </button>
      </div>
    );
  };

  return (
    <Modal
      isOpen={true}
      onRequestClose={onClose}
      className="tier-detail-modal"
      overlayClassName="modal-overlay"
    >
      <div className="modal-header">
        <h2>{coachName} — {season} {year}</h2>
        <button onClick={onClose} className="close-btn">×</button>
      </div>

      {loading ? (
        <Loader size={40} />
      ) : (
        <>
          <div className="modal-tabs">
            <button
              className={activeTab === 'overview' ? 'active' : ''}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={activeTab === 'complaints' ? 'active' : ''}
              onClick={() => setActiveTab('complaints')}
            >
              Complaints
            </button>
            <button
              className={activeTab === 'positive' ? 'active' : ''}
              onClick={() => setActiveTab('positive')}
            >
              Positive Signals
            </button>
            <button
              className={activeTab === 'endorsement' ? 'active' : ''}
              onClick={() => setActiveTab('endorsement')}
            >
              Review
            </button>
          </div>

          <div className="modal-content">
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'complaints' && renderComplaints()}
            {activeTab === 'positive' && renderPositiveSignals()}
            {activeTab === 'endorsement' && renderEndorsement()}
          </div>
        </>
      )}
    </Modal>
  );
}

export default CoachTierDetailModal;
